/**
 * FulfillmentDetail Page
 * 
 * Displays detailed information for a single fulfillment with admin actions.
 * Desktop: Clean sectioned layout with unified appearance
 * Mobile: Card-based layout for better scrolling
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { FulfillmentStatusBadge, RewardCategoryBadge } from '@/components/common/Badge';
import { FulfillmentContextDisplay } from '@/components/fulfillments/FulfillmentContextDisplay';
import { fulfillmentService } from '@/app/services/fulfillmentService';
import { auditService } from '@/app/services';
import type { FulfillmentResponse, FulfillmentAction } from '@/app/types/fulfillment';
import { isActionAvailable, getTargetStatus } from '@/app/types/fulfillment';
import './FulfillmentDetail.scss';

export const FulfillmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [fulfillment, setFulfillment] = useState<FulfillmentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<FulfillmentAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<FulfillmentAction | null>(null);
  const [actionReason, setActionReason] = useState('');

  useEffect(() => {
    const fetchFulfillment = async () => {
      if (!id) {
        setError('Fulfillment ID is missing');
        setLoading(false);
        return;
      }

      try {
        const data = await fulfillmentService.getFulfillmentById(id);
        if (data) {
          setFulfillment(data);
          
          // Log record view for audit
          auditService.logRecordView('fulfillment', id, {
            page_url: `/fulfillments/${id}`,
          }).catch((err) => logger.warn('Failed to log record view', { error: err }));
        } else {
          setError('Fulfillment not found');
        }
      } catch (err) {
        setError('Failed to load fulfillment');
        logger.error('Failed to load fulfillment', { error: err, fulfillmentId: id });
      } finally {
        setLoading(false);
      }
    };

    fetchFulfillment();
  }, [id]);

  const executeAction = useCallback(async (action: FulfillmentAction) => {
    if (!id || !fulfillment) return;

    setActionLoading(action);
    try {
      const targetStatus = getTargetStatus(action);
      const result = await fulfillmentService.updateFulfillment(id, {
        status: targetStatus,
        reason: actionReason || undefined,
      });

      if (result) {
        setFulfillment(result);
        
        await auditService.logRecordModification(
          'updated',
          'fulfillment',
          id,
          {
            action_type: action,
            reason: actionReason,
            previous_status: fulfillment.status,
            new_status: result.status,
          }
        );
      } else {
        setError(`Failed to ${action.toLowerCase()} fulfillment`);
      }
    } catch (err) {
      setError(`Failed to ${action.toLowerCase()} fulfillment`);
      logger.error(`Failed to ${action.toLowerCase()} fulfillment`, { error: err, fulfillmentId: id, action });
    } finally {
      setActionLoading(null);
      setShowConfirmDialog(null);
      setActionReason('');
    }
  }, [id, fulfillment, actionReason]);

  const handleActionClick = useCallback((action: FulfillmentAction) => {
    setShowConfirmDialog(action);
    setActionReason('');
  }, []);

  const getActionConfig = (action: FulfillmentAction) => {
    const configs: Record<FulfillmentAction, { title: string; description: string; buttonText: string; buttonClass: string }> = {
      RETRY: {
        title: 'Retry Fulfillment',
        description: 'This will attempt to re-process the fulfillment. The outcome may vary based on the current state of external systems.',
        buttonText: 'Retry',
        buttonClass: 'btn-retry',
      },
      COMPLETE: {
        title: 'Mark as Completed',
        description: 'This will manually mark the fulfillment as completed. Use this when you have confirmed the fulfillment was processed outside the system.',
        buttonText: 'Mark Complete',
        buttonClass: 'btn-complete',
      },
      CANCEL: {
        title: 'Cancel Fulfillment',
        description: 'This will cancel the pending fulfillment. The user may need to be notified separately.',
        buttonText: 'Cancel',
        buttonClass: 'btn-cancel',
      },
    };
    return configs[action];
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className="fulfillment-detail-page">
        <div className="loading">Loading fulfillment details...</div>
      </div>
    );
  }

  if (error || !fulfillment) {
    return (
      <div className="fulfillment-detail-page">
        <div className="error-state">
          <p>{error || 'Fulfillment not found'}</p>
          <button onClick={() => navigate('/fulfillments')}>Back to Fulfillments</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fulfillment-detail-page">
      <div className="back-nav">
        <button onClick={() => navigate('/fulfillments')} className="btn-back">
          ← Back to Fulfillments
        </button>
      </div>

      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-badges">
            <FulfillmentStatusBadge status={fulfillment.status} />
            {fulfillment.reward_category && (
              <RewardCategoryBadge category={fulfillment.reward_category} />
            )}
          </div>
        </div>
        <div className="header-actions">
          {isActionAvailable(fulfillment.status, 'RETRY') && (
            <button
              className="btn-action btn-retry"
              onClick={() => handleActionClick('RETRY')}
              disabled={actionLoading !== null}
            >
              🔄 Retry
            </button>
          )}
          {isActionAvailable(fulfillment.status, 'COMPLETE') && (
            <button
              className="btn-action btn-complete"
              onClick={() => handleActionClick('COMPLETE')}
              disabled={actionLoading !== null}
            >
              ✓ Mark Complete
            </button>
          )}
          {isActionAvailable(fulfillment.status, 'CANCEL') && (
            <button
              className="btn-action btn-cancel"
              onClick={() => handleActionClick('CANCEL')}
              disabled={actionLoading !== null}
            >
              ✕ Cancel
            </button>
          )}
        </div>
      </div>

      {/* Failure Alert */}
      {fulfillment.failure_reason && (
        <div className="failure-alert">
          <span className="alert-icon">⚠️</span>
          <div className="alert-content">
            <strong>Failure Reason:</strong>
            <p>{fulfillment.failure_reason}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="detail-container">
        {/* Primary Info Section */}
        <section className="detail-section">
          <h2 className="section-title">Fulfillment Information</h2>
          <div className="info-grid">
            <div className="info-row">
              <span className="info-label">Fulfillment ID</span>
              <span className="info-value mono">
                {fulfillment.fulfillment_id}
                <button className="copy-btn" onClick={() => copyToClipboard(fulfillment.fulfillment_id)}>📋</button>
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">User ID</span>
              <span className="info-value mono">
                {fulfillment.user_id}
                <button className="copy-btn" onClick={() => copyToClipboard(fulfillment.user_id)}>📋</button>
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Status</span>
              <span className="info-value">
                <FulfillmentStatusBadge status={fulfillment.status} />
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">External Tracking ID</span>
              <span className="info-value mono">
                {fulfillment.external_tracking_id || '—'}
                {fulfillment.external_tracking_id && (
                  <button className="copy-btn" onClick={() => copyToClipboard(fulfillment.external_tracking_id!)}>📋</button>
                )}
              </span>
            </div>
          </div>
        </section>

        {/* Related Entities Section */}
        <section className="detail-section">
          <h2 className="section-title">Related Records</h2>
          <div className="info-grid two-col">
            <div className="info-group">
              <h3 className="group-title">Reward</h3>
              <div className="info-row">
                <span className="info-label">Name</span>
                <span className="info-value">
                  <Link to={`/rewards/${fulfillment.reward_id}`} className="entity-link">
                    {fulfillment.reward_name || 'View Reward'} →
                  </Link>
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Category</span>
                <span className="info-value">
                  {fulfillment.reward_category ? (
                    <RewardCategoryBadge category={fulfillment.reward_category} />
                  ) : '—'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Reward ID</span>
                <span className="info-value mono small">
                  {fulfillment.reward_id}
                  <button className="copy-btn" onClick={() => copyToClipboard(fulfillment.reward_id)}>📋</button>
                </span>
              </div>
            </div>
            <div className="info-group">
              <h3 className="group-title">Transaction</h3>
              <div className="info-row">
                <span className="info-label">Link</span>
                <span className="info-value">
                  <Link to={`/transactions/${fulfillment.transaction_id}`} className="entity-link">
                    View Transaction →
                  </Link>
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Transaction ID</span>
                <span className="info-value mono small">
                  {fulfillment.transaction_id}
                  <button className="copy-btn" onClick={() => copyToClipboard(fulfillment.transaction_id)}>📋</button>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Timestamps Section */}
        <section className="detail-section">
          <h2 className="section-title">Timeline</h2>
          <div className="timeline-grid">
            <div className="timeline-item">
              <span className="timeline-label">Initiated</span>
              <span className="timeline-value">{new Date(fulfillment.initiated_at).toLocaleString()}</span>
            </div>
            {fulfillment.completed_at && (
              <div className="timeline-item success">
                <span className="timeline-label">Completed</span>
                <span className="timeline-value">{new Date(fulfillment.completed_at).toLocaleString()}</span>
              </div>
            )}
            {fulfillment.failed_at && (
              <div className="timeline-item error">
                <span className="timeline-label">Failed</span>
                <span className="timeline-value">{new Date(fulfillment.failed_at).toLocaleString()}</span>
              </div>
            )}
            <div className="timeline-item muted">
              <span className="timeline-label">Created</span>
              <span className="timeline-value">{new Date(fulfillment.created_at).toLocaleString()}</span>
            </div>
            <div className="timeline-item muted">
              <span className="timeline-label">Updated</span>
              <span className="timeline-value">{new Date(fulfillment.updated_at).toLocaleString()}</span>
            </div>
          </div>
        </section>

        {/* Context Section */}
        <section className="detail-section">
          <h2 className="section-title">Context Data</h2>
          <FulfillmentContextDisplay 
            context={fulfillment.context_json} 
            rewardCategory={fulfillment.reward_category}
          />
        </section>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="confirm-overlay" onClick={() => setShowConfirmDialog(null)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{getActionConfig(showConfirmDialog).title}</h3>
            <p className="dialog-description">
              {getActionConfig(showConfirmDialog).description}
            </p>
            
            <div className="dialog-field">
              <label>Reason (optional)</label>
              <textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Enter a reason for this action..."
                rows={3}
              />
            </div>

            <div className="dialog-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowConfirmDialog(null)}
                disabled={actionLoading !== null}
              >
                Cancel
              </button>
              <button
                className={getActionConfig(showConfirmDialog).buttonClass}
                onClick={() => executeAction(showConfirmDialog)}
                disabled={actionLoading !== null}
              >
                {actionLoading === showConfirmDialog 
                  ? 'Processing...' 
                  : getActionConfig(showConfirmDialog).buttonText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FulfillmentDetail;
